import { useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import { loadPostCollectionAsync, resetPostCollection } from '../../data-layer/posts/actions';
import {
    selectPostCollection,
    selectPostCollectionLoading,
} from '../../data-layer/posts/selectors';
import { useDispatch } from '../../data-layer/useDispatch';
import { useSelector } from '../../data-layer/useSelector';
import type { PostModel } from '../../data-layer/posts/types';

const THROTTLE_TIMEOUT = 1000;

export type PostCollectionFilters = Omit<Parameters<typeof loadPostCollectionAsync>[1], 'next'>;

export interface UsePostsCollectionPayload {
    items: PostModel[];
    isLoading: boolean;
    isLoaded: boolean;
    isEndReached: boolean;
    onReset: VoidFunction;
    filters: Record<string, any>;
    onLoadNext: (params?: Record<string, any>) => void;
    onUpdateQuery: (filters: Partial<PostCollectionFilters>) => void;
    next: string;
}

export interface UsePostsCollectionProps {
    namespace: string;
    filters: PostCollectionFilters;
    forceFiltersUpdate?: boolean;
}

export const usePostCollection = (params: UsePostsCollectionProps): UsePostsCollectionPayload => {
    const { namespace, forceFiltersUpdate } = params;
    const filtersRef = useRef<PostCollectionFilters>(params.filters);
    const dispatch = useDispatch();
    const { items = [], next } = useSelector((state) => selectPostCollection(state, namespace));
    const isLoading = useSelector((state) => selectPostCollectionLoading(state, namespace));
    const isEndReached = items.length !== 0 && !next;
    const [isLoaded, setIsLoaded] = useState(false);
    const prevLoadingRef = useRef<boolean>();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onLoadNext = useCallback(
        throttle(() => {
            // Не отправляем запрос если закончились посты на бэке
            if (items.length && !next) {
                return;
            }

            dispatch(
                loadPostCollectionAsync(namespace, {
                    ...(forceFiltersUpdate ? params.filters : filtersRef.current),
                    next,
                }),
            );
        }, THROTTLE_TIMEOUT),
        [dispatch, namespace, filtersRef, forceFiltersUpdate, next, items.length],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onReset = useCallback(() => dispatch(resetPostCollection(namespace)), [dispatch]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onUpdateQuery = useCallback(
        throttle((filters: Partial<PostCollectionFilters>) => {
            onReset();
            filtersRef.current = {
                ...(forceFiltersUpdate ? params.filters : filtersRef.current),
                ...filters,
            };
            dispatch(loadPostCollectionAsync(namespace, filtersRef.current));
        }, THROTTLE_TIMEOUT),
        [dispatch, namespace, onReset, forceFiltersUpdate],
    );

    // TODO: Позже сделать первоначальную загрузку на базе бесконечных списков/сеток
    useEffect(() => {
        if (items.length === 0) {
            onLoadNext();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [namespace]);

    useEffect(() => {
        if (prevLoadingRef.current === true && !isLoading) {
            setIsLoaded(true);
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    return {
        isLoading,
        isLoaded,
        items,
        onReset,
        onLoadNext,
        onUpdateQuery,
        isEndReached,
        filters: filtersRef.current,
        next,
    };
};
